import type { Action } from "redux";
import type {
    AccentColourType,
    ColourType,
    ExtraSetItemType,
    ExtraType,
    PackType,
    PersonalisationItemType,
    PersonalizationType,
    PriceType,
    PromotionType,
    ServiceProductType,
    UpholsteryType,
    WheelType,
    WinterWheelType,
} from "../../../../../common-deprecated/types/CommonTypes";
import type { ConfigurationType } from "../../../../shared-logic/utils/ValidateConfig";
import type { BasketType } from "../../../../shared-logic/types/BasketType";
import type { BuildAndBuyDispatchType } from "../store";

export const POP_BASKET_HISTORY = "basket/popBasketHistory" as const;
export const PUSH_HISTORY = "basket/pushHistory" as const;

export const INIT_BASKET = "basket/init" as const;
export const RESET_BASKET = "reset/basket" as const;

export const SET_COLOUR = "basket/setColour" as const;
export const SET_PRIMARY_ACCENT_COLOUR = "basket/setPrimaryAccentColour" as const;
export const SET_SECONDARY_ACCENT_COLOUR = "basket/setSecondaryAccentColour" as const;

export const SET_WHEEL = "basket/setWheel" as const;

export const SET_WINTER_WHEEL = "basket/setWinterWheel" as const;
export const REMOVE_WINTER_WHEEL = "basket/removeWinterWheel" as const;

export const ADD_EXTRA = "basket/addExtra" as const;
export const REMOVE_EXTRA = "basket/removeExtra" as const;
export const RESOLVE_EXTRA_CONFLICTS = "basket/resolveConflictExtra" as const;
export const SET_EXTRA_OPTION = "basket/setExtraOption" as const;
export const UPDATE_EXTRA_SET_ITEMS = "basket/updateExtraSetItems" as const;

export const ADD_PACK = "basket/addPack" as const;
export const RESOLVE_CONFLICT_PACK = "basket/resolveConflictPack" as const;
export const REMOVE_PACK = "basket/removePack" as const;
export const ADD_PACK_EXTRA = "basket/addPackExtra" as const;
export const REMOVE_PACK_EXTRA = "basket/removePackExtra" as const;
export const SET_UPHOLSTERY = "basket/setUpholstery" as const;
export const CALCULATE_PRICE = "basket/calculatePrice" as const;

export const ADD_FINANCE_PACK = "basket/addFinancePack" as const;
export const REMOVE_FINANCE_PACK = "basket/removeFinancePack" as const;
export const ADD_FINANCE_PACK_EXTRAS = "basket/addFinancePackExtras" as const;
export const REMOVE_FINANCE_PACK_EXTRAS = "basket/removeFinancePackExtras" as const;
export const ADD_FINANCE_PACK_SERVICEPRODUCTS = "basket/addFinancePackServiceProducts" as const;
export const REMOVE_FINANCE_PACK_SERVICEPRODUCTS = "basket/removeFinancePackServiceProducts" as const;

export const SET_CONFIGURATION = "basket/setValidatedConfiguration" as const;

export const PUSH_USER_SELECTED = "basket/pushUserSelected" as const;
export const LOAD_USER_SELECTED = "basket/loadUserSelected" as const;

export const ADD_USER_SELECTED_PROMOTION = "basket/addUserSelectedPromotion" as const;
export const REMOVE_USER_SELECTED_PROMOTION = "basket/removeUserSelectedPromotion" as const;

export const ADD_SERVICE_PRODUCT = "basket/addServiceProduct" as const;
export const REMOVE_SERVICE_PRODUCT = "basket/removeServiceProduct" as const;
export const RESOLVE_SERVICE_PRODUCT_CONFLICT = "basket/resolveConflictServiceProduct" as const;

//---------------------------------------------------------
// User selected logic constants
//---------------------------------------------------------
export const USER_SELECT_COLOUR = "colour";
export const USER_SELECT_UPHOLSTERY = "upholstery";
export const USER_SELECT_WHEEL = "wheel";

//---------------------------------------------------------
// General actions
//---------------------------------------------------------
export type PopBasketHistoryType = {
    type: typeof POP_BASKET_HISTORY;
    transaction: string | boolean;
    [index: string]: any;
};
export const popBasketHistory = (
    value: { [id: string]: any },
    transaction: string | boolean = false,
): PopBasketHistoryType => ({
    type: POP_BASKET_HISTORY,
    ...value,
    transaction,
});
export type PushHistoryType = { type: typeof PUSH_HISTORY };
export const pushHistory = (): PushHistoryType => ({ type: PUSH_HISTORY });

type InitBasketType = { type: typeof INIT_BASKET; basket: BasketType };
export const initBasket = (basket: BasketType): InitBasketType => ({ type: INIT_BASKET, basket });

type ResetBasketType = { type: typeof RESET_BASKET; basket: BasketType };
export const resetBasket = (basket: BasketType): ResetBasketType => ({ type: RESET_BASKET, basket });

export type SetColourType = {
    type: typeof SET_COLOUR;
    colour: ColourType;
    userSelected: boolean;
    transaction: string | boolean;
};
export const setColour = (
    colour: ColourType,
    userSelected: boolean = false,
    transaction: string | boolean = false,
): SetColourType => ({
    type: SET_COLOUR,
    colour,
    userSelected,
    transaction,
});

export type SetPrimaryAccentColourType = {
    type: typeof SET_PRIMARY_ACCENT_COLOUR;
    pack: PackType;
    colour: AccentColourType;
    parentAction: Action | false;
    userSelected: boolean;
    transaction: boolean | string;
    _snapshot?: BasketType;
};
export const setPrimaryAccentColour = (
    pack: PackType,
    colour: AccentColourType,
    parentAction: Action | false,
    userSelected: boolean = false,
    transaction: boolean | string,
): SetPrimaryAccentColourType => ({
    type: SET_PRIMARY_ACCENT_COLOUR,
    colour,
    pack,
    parentAction,
    userSelected,
    transaction,
});

export type SetSecondaryAccentColourType = {
    type: typeof SET_SECONDARY_ACCENT_COLOUR;
    pack: PackType;
    colour: AccentColourType;
    parentAction: Action | false;
    userSelected: boolean;
    transaction: boolean | string;
    _snapshot?: BasketType;
};
export const setSecondaryAccentColour = (
    pack: PackType,
    colour: AccentColourType,
    parentAction: Action | false,
    userSelected: boolean = false,
    transaction: boolean | string = false,
): SetSecondaryAccentColourType => ({
    type: SET_SECONDARY_ACCENT_COLOUR,
    colour,
    pack,
    parentAction,
    userSelected,
    transaction,
});

type SetWinterWheelType = {
    type: typeof SET_WINTER_WHEEL;
    winterWheel: WinterWheelType;
    transaction: string | boolean;
};
export const setWinterWheel = (
    winterWheel: WinterWheelType,
    transaction: boolean | string = false,
): SetWinterWheelType => ({
    type: SET_WINTER_WHEEL,
    winterWheel,
    transaction,
});

type RemoveWinterWheelType = { type: typeof REMOVE_WINTER_WHEEL; transaction: boolean };
export const removeWinterWheel = (): RemoveWinterWheelType => ({
    type: REMOVE_WINTER_WHEEL,
    transaction: true,
});

export type SetWheelType = {
    type: typeof SET_WHEEL;
    wheel: WheelType;
    userSelected: boolean;
    transaction: boolean | string;
    extraPendingStack?: ExtraType[];
    previousWheel?: WheelType;
};
export const setWheel = (
    wheel: WheelType,
    userSelected: boolean = false,
    transaction: boolean | string = false,
    extraPendingStack?: ExtraType[],
): SetWheelType => ({
    type: SET_WHEEL,
    wheel,
    userSelected,
    transaction,
    extraPendingStack,
});

export type SetUpholsteryType = {
    type: typeof SET_UPHOLSTERY;
    upholstery: UpholsteryType;
    userSelected: boolean;
    transaction: string | boolean;
};
export const setUpholstery = (
    upholstery: UpholsteryType,
    userSelected = false,
    transaction: string | boolean = false,
): SetUpholsteryType => ({
    type: SET_UPHOLSTERY,
    upholstery,
    userSelected,
    transaction,
});

export type CalculatePriceType = {
    type: typeof CALCULATE_PRICE;
    loadExternalPrice: boolean;
    firstTime: boolean;
    total?: PriceType;
    previousTotal?: PriceType;
};
// loadExternalPrice is only used in context when external prices are enabled see BasketLogic.js and ExternalPriceLogic.ts.
// firstTime is only used in combination with external prices as well, this is to determine whether to load the monthly rates or initialize the quote configuration
export const calculatePrice = (
    loadExternalPrice: boolean = true,
    firstTime: boolean = false,
    total?: PriceType,
): CalculatePriceType => ({
    type: CALCULATE_PRICE,
    loadExternalPrice,
    firstTime,
    total,
});

type PushUserSelectedType = {
    type: typeof PUSH_USER_SELECTED;
    selectedType: string;
    id: string;
};

export const pushUserSelected = (selectedType: string, id: string): PushUserSelectedType => ({
    type: PUSH_USER_SELECTED,
    id,
    selectedType,
});

type LoadUserSelectedType = {
    type: typeof LOAD_USER_SELECTED;
    userSelected: BasketType["userSelected"];
    transaction: string | boolean;
};
export const loadUserSelected = (
    userSelected: BasketType["userSelected"],
    transaction: string | boolean = false,
): LoadUserSelectedType => ({
    type: LOAD_USER_SELECTED,
    userSelected,
    transaction,
});

type AddUserSelectedPromotionType = {
    type: typeof ADD_USER_SELECTED_PROMOTION;
    promotion: PromotionType;
    transaction: true;
};
export const addUserSelectedPromotion = (promotion: PromotionType): AddUserSelectedPromotionType => ({
    type: ADD_USER_SELECTED_PROMOTION,
    promotion,
    transaction: true,
});

type RemoveUserSelectedPromotionType = {
    type: typeof REMOVE_USER_SELECTED_PROMOTION;
    promotion: PromotionType;
    transaction: true;
};
export const removeUserSelectedPromotion = (promotion: PromotionType): RemoveUserSelectedPromotionType => ({
    type: REMOVE_USER_SELECTED_PROMOTION,
    promotion,
    transaction: true,
});

//---------------------------------------------------------
// Extra actions
//---------------------------------------------------------
export type AddExtraType = {
    type: typeof ADD_EXTRA;
    extra: ExtraType;
    automatic: boolean;
    transaction: boolean | string;
    optionId: string;
    extraPendingStack?: ExtraType[];
    setItems?: ExtraSetItemType[];
};
export const addExtra = (
    extra: ExtraType,
    automatic: boolean,
    transaction: boolean | string = false,
    optionId: string = "",
    extraPendingStack?: ExtraType[],
    setItems?: ExtraSetItemType[],
): AddExtraType =>
    // Add optionId straight to the action extra here. This makes getting the optionId while validating a lot easier.
    // TODO Add this in a test so we dont miss this.
    ({
        type: ADD_EXTRA,
        extra: { ...extra, selectedOptionId: optionId },
        automatic,
        transaction,
        optionId,
        extraPendingStack,
        setItems,
    });

export type SetExtraOptionType = {
    type: typeof SET_EXTRA_OPTION;
    extra: ExtraType;
    optionId: string;
    transaction: boolean | string;
};
export const setExtraOption = (
    extra: ExtraType,
    optionId: string,
    transaction: boolean | string = false,
): SetExtraOptionType => ({
    type: SET_EXTRA_OPTION,
    extra: { ...extra, selectedOptionId: optionId },
    optionId,
    transaction,
});

type UpdateExtraSetItemsType = {
    type: typeof UPDATE_EXTRA_SET_ITEMS;
    extra: ExtraType;
    setItems: ExtraSetItemType[];
    transaction: boolean | string;
};

export const updateExtraSetItems = (
    extra: ExtraType,
    setItems: ExtraSetItemType[],
    transaction: boolean | string = false,
): UpdateExtraSetItemsType => ({
    type: UPDATE_EXTRA_SET_ITEMS,
    extra,
    setItems,
    transaction,
});

export type RemoveExtraType = {
    type: typeof REMOVE_EXTRA;
    extra: ExtraType;
    ignoreIncludes: boolean;
    transaction: string | boolean;
};
export const removeExtra = (
    extra: ExtraType,
    ignoreIncludes: boolean,
    transaction: string | boolean = false,
): RemoveExtraType => ({
    type: REMOVE_EXTRA,
    extra,
    ignoreIncludes,
    transaction,
});

export type ResolveExtraConflictType = {
    type: typeof RESOLVE_EXTRA_CONFLICTS;
    extra: ExtraType;
    selectedExtra: ExtraType;
    transaction: boolean | string;
    extraPendingStack: ExtraType[];
};
export const resolveExtraConflicts = (
    extra: ExtraType,
    selectedExtra: ExtraType,
    transaction: boolean | string = false,
    extraPendingStack: ExtraType[],
): ResolveExtraConflictType => ({
    type: RESOLVE_EXTRA_CONFLICTS,
    extra,
    selectedExtra,
    transaction,
    extraPendingStack,
});

//---------------------------------------------------------
// financePack actions
//---------------------------------------------------------
export type AddFinancePackType = {
    type: typeof ADD_FINANCE_PACK;
    financePack: PackType;
    transaction: string | boolean;
};

export const addFinancePack = (financePack: PackType, transaction: boolean | string = false): AddFinancePackType => ({
    type: ADD_FINANCE_PACK,
    financePack,
    transaction,
});

export type RemoveFinancePackType = {
    type: typeof REMOVE_FINANCE_PACK;
    financePack: PackType;
    transaction: boolean | string;
};

export const removeFinancePack = (
    financePack: PackType,
    transaction: boolean | string = false,
): RemoveFinancePackType => ({
    type: REMOVE_FINANCE_PACK,
    financePack,
    transaction,
});

export type AddFinancePackExtrasType = {
    type: typeof ADD_FINANCE_PACK_EXTRAS;
    financePack: PackType;
    extra: ExtraType;
    transaction: boolean | string;
};

export const addFinancePackExtras = (
    financePack: PackType,
    extra: ExtraType,
    transaction: boolean | string = false,
): AddFinancePackExtrasType => ({
    type: ADD_FINANCE_PACK_EXTRAS,
    financePack,
    extra,
    transaction,
});

export type RemoveFinancePackExtrasType = {
    type: typeof REMOVE_FINANCE_PACK_EXTRAS;
    extra: ExtraType;
    transaction: boolean | string;
};

export const removeFinancePackExtra = (
    extra: ExtraType,
    transaction: boolean | string = false,
): RemoveFinancePackExtrasType => ({
    type: REMOVE_FINANCE_PACK_EXTRAS,
    extra,
    transaction,
});

export type AddFinancePackServiceProductsType = {
    type: typeof ADD_FINANCE_PACK_SERVICEPRODUCTS;
    financePack: PackType;
    serviceProduct: ServiceProductType;
    transaction: boolean | string;
};

export const addFinancePackServiceProducts = (
    financePack: PackType,
    serviceProduct: ServiceProductType,
    transaction: boolean | string = false,
): AddFinancePackServiceProductsType => ({
    type: ADD_FINANCE_PACK_SERVICEPRODUCTS,
    financePack,
    serviceProduct,
    transaction,
});

export type RemoveFinancePackServiceProductsType = {
    type: typeof REMOVE_FINANCE_PACK_SERVICEPRODUCTS;
    serviceProduct: ServiceProductType;
    transaction: boolean | string;
};

export const removeFinancePackServiceProducts = (
    serviceProduct: ServiceProductType,
    transaction: boolean | string = false,
): RemoveFinancePackServiceProductsType => ({
    type: REMOVE_FINANCE_PACK_SERVICEPRODUCTS,
    serviceProduct,
    transaction,
});
//---------------------------------------------------------
// Pack actions
//
// The selectedExtra parameters are primarily used during conflict resolution to contain the additional
// selected parameters during resolution.
// The 'automatic' parameter is also used during conflict resolution to signal the action is called during resolution.
//---------------------------------------------------------
export type AddPackType = {
    type: typeof ADD_PACK;
    pack: PackType;
    extra?: ExtraType;
    selectedExtras: ExtraType[];
    selectedPacks: PackType[];
    automatic: boolean;
    parentAction: Action | false;
    userSelected: boolean;
    transaction: string | boolean;
    _snapshot?: BasketType;
};
export const addPack = (
    pack: PackType,
    selectedExtras: ExtraType[] = [],
    selectedPacks: PackType[] = [],
    automatic: boolean = false,
    parentAction: Action | false,
    userSelected: boolean = false,
    transaction: boolean | string = false,
): AddPackType => ({
    type: ADD_PACK,
    pack,
    selectedExtras,
    selectedPacks,
    automatic,
    parentAction,
    userSelected,
    transaction,
});

export type ResolvePackConflictType = {
    type: typeof RESOLVE_CONFLICT_PACK;
    pack: PackType;
    selectedItem: ExtraType | PackType;
    conflicts: ExtraType[] | PackType[];
    selectedConflicts: ExtraType[] | PackType[];
    isConflictBetweenPackExtras: boolean;
    transaction: boolean;
};
export const resolvePackConflict = (
    pack: PackType,
    selectedItem: ExtraType | PackType,
    conflicts: ExtraType[] | PackType[],
    selectedConflicts: ExtraType[] | PackType[],
    isConflictBetweenPackExtras: boolean,
): ResolvePackConflictType => ({
    type: RESOLVE_CONFLICT_PACK,
    pack,
    selectedItem,
    conflicts,
    selectedConflicts,
    isConflictBetweenPackExtras,
    transaction: true,
});

type RemovePackType = { type: typeof REMOVE_PACK; pack: PackType; transaction: string | boolean };
export const removePack = (pack: PackType, transaction: boolean | string = false): RemovePackType => ({
    type: REMOVE_PACK,
    pack,
    transaction,
});

/**
 * pack - Pack of which the extra is part of.
 * extra - Extra which is added.
 * selectedExtras - Additional extras being selected. Usually used during conflict resolution.
 */
export type AddPackExtraType = {
    type: typeof ADD_PACK_EXTRA;
    pack: PackType;
    extra: ExtraType;
    selectedExtras: ExtraType[];
    selectedPacks?: PackType[];
    automatic: boolean;
    transaction: boolean | string;
    combinedSelectedExtras?: ExtraType[];
    parentAction: Action | false;
    _snapshot?: BasketType;
};
export const addPackExtra = (
    pack: PackType,
    extra: ExtraType,
    selectedExtras: ExtraType[] = [],
    automatic: boolean = false,
    transaction: string | boolean = false,
    parentAction: Action | false,
    combinedSelectedExtras?: ExtraType[],
): AddPackExtraType => ({
    type: ADD_PACK_EXTRA,
    pack,
    selectedExtras,
    extra,
    automatic,
    transaction,
    parentAction,
    combinedSelectedExtras,
});

type RemovePackExtraType = {
    type: typeof REMOVE_PACK_EXTRA;
    pack: PackType;
    extra: ExtraType;
    transaction: boolean | string;
};
export const removePackExtra = (
    pack: PackType,
    extra: ExtraType,
    transaction: boolean | string = false,
): RemovePackExtraType => ({
    type: REMOVE_PACK_EXTRA,
    pack,
    extra,
    transaction,
});

export const setPersonalization = (
    dispatch: BuildAndBuyDispatchType,
    item: PersonalisationItemType,
    itemType: PersonalizationType,
): void => {
    switch (itemType) {
        case "upholstery":
            dispatch(setUpholstery(item as UpholsteryType, true, true));
            break;

        case "colour":
            dispatch(setColour(item as ColourType, true, true));
            break;

        case "wheel":
            dispatch(setWheel(item as WheelType, true, true));
            break;
    }
};

export type SetValidatedConfigurationType = {
    type: typeof SET_CONFIGURATION;
    configuration: ConfigurationType;
};

export const setValidatedConfiguration = (configuration: ConfigurationType): SetValidatedConfigurationType => ({
    type: SET_CONFIGURATION,
    configuration,
});

export type ResolveServiceProductConflictType = {
    type: typeof RESOLVE_SERVICE_PRODUCT_CONFLICT;
    serviceProduct: ServiceProductType;
    selectedItem: ServiceProductType;
    conflicts: ServiceProductType[];
};
export const resolveServiceProductConflict = (
    serviceProduct: ServiceProductType,
    selectedItem: ServiceProductType,
    conflicts: ServiceProductType[],
): ResolveServiceProductConflictType => ({
    type: RESOLVE_SERVICE_PRODUCT_CONFLICT,
    serviceProduct,
    selectedItem,
    conflicts,
});

export type AddServiceProductType = {
    type: typeof ADD_SERVICE_PRODUCT;
    serviceProduct: ServiceProductType;
    transaction?: boolean | string;
    userSelected?: boolean; // Property to see if the serviceProduct was added by user/logic as these have different logic behind it
};

export const addServiceProduct = (
    serviceProduct: ServiceProductType,
    transaction: boolean | string = false,
    userSelected: boolean,
): AddServiceProductType => ({
    type: ADD_SERVICE_PRODUCT,
    serviceProduct,
    transaction,
    userSelected,
});

export type RemoveServiceProductType = {
    type: typeof REMOVE_SERVICE_PRODUCT;
    serviceProduct: ServiceProductType;
    transaction?: boolean | string;
};

export const removeServiceProduct = (
    serviceProduct: ServiceProductType,
    transaction: boolean | string = false,
): RemoveServiceProductType => ({
    type: REMOVE_SERVICE_PRODUCT,
    serviceProduct,
    transaction,
});

export type BasketActionsType =
    | SetWheelType
    | SetColourType
    | SetUpholsteryType
    | CalculatePriceType
    | PopBasketHistoryType
    | LoadUserSelectedType
    | RemoveWinterWheelType
    | AddUserSelectedPromotionType
    | RemoveUserSelectedPromotionType
    | RemovePackType
    | RemoveExtraType
    | SetWinterWheelType
    | SetWinterWheelType
    | SetWinterWheelType
    | AddPackType
    | AddPackExtraType
    | AddServiceProductType
    | RemoveServiceProductType
    | RemovePackExtraType
    | SetPrimaryAccentColourType
    | SetSecondaryAccentColourType
    | AddExtraType
    | SetExtraOptionType
    | ResolveExtraConflictType
    | ResolvePackConflictType
    | UpdateExtraSetItemsType
    | PushHistoryType
    | InitBasketType
    | ResetBasketType
    | SetValidatedConfigurationType
    | PushUserSelectedType
    | ResolveServiceProductConflictType
    | AddFinancePackType
    | RemoveFinancePackType
    | AddFinancePackExtrasType
    | RemoveFinancePackExtrasType
    | AddFinancePackServiceProductsType
    | RemoveFinancePackServiceProductsType;
